import { PBSTCK_BIDDER_SEAT_CODE } from '@pubstack/common/src/bidder-seat'
import { DEVICE_TYPES } from '@pubstack/common/src/device-type'
import { PUBSTACK_BIDDER } from '@pubstack/common/src/kleanadsOutput'
import { AdmStackOutput, isAdmStack, Stack } from '@pubstack/common/src/stack'
import type { IconName } from '~/components/Icon'
import { uniq } from 'lodash-es'
import { uniqueBidders } from './useStackChangelog'

/** What a One stack serves: its output, or its snapshot while nothing is deployed. Nothing for a Node stack. */
export const admOutputOf = (stack: Stack): AdmStackOutput | undefined => (isAdmStack(stack) ? (stack.output ?? stack.snapshot) : undefined)

/**
 * The ways a One stack calls Prebid Server: its legacy seats, which the device `s2sConfig` routes, and the seats
 * of the server-side variant of ADR-0036, reached through the pubstack bidder. Those are told apart in two:
 * Spark Demand, and the server-side and Spark for Pubs seats.
 */
export type ServersideRoutes = Readonly<{ legacy: boolean; sparkDemand: boolean; serverSide: boolean }>

export const serversideRoutesOf = (stack: Stack): ServersideRoutes => {
  const output = admOutputOf(stack)
  const variant = output?.serverside
  // The variant names each of its seats under its adapter, and Spark Demand is the seat on Pubstack's own one.
  // A Node seat and a Spark for Pubs one both ride an SSP adapter, so the output cannot tell them apart, and
  // they share a qualifier.
  const isSparkDemand = (bidderName: string): boolean => variant?.bidderAliases[bidderName] === PBSTCK_BIDDER_SEAT_CODE
  const { legacyServerSide, serverSide: variantBidders } = admBidderGroups(output)
  return {
    legacy: legacyServerSide.length > 0,
    sparkDemand: variantBidders.some(isSparkDemand),
    serverSide: variantBidders.some((bidderName) => !isSparkDemand(bidderName)),
  }
}

export type ServersideQualifier = Readonly<{ key: keyof ServersideRoutes; iconName: IconName; tooltipText: string; active: boolean }>

/**
 * The qualifiers of the Server-side column of the stack list, one per route, drawn like the rule ones: the
 * legacy seats Prebid.js routes from the page, the server-side and Spark for Pubs seats, then Spark Demand.
 */
export const serversideQualifiersOf = ({ legacy, sparkDemand, serverSide }: ServersideRoutes): readonly ServersideQualifier[] => [
  { key: 'legacy', iconName: 'site', tooltipText: legacy ? 'Server-side One (legacy) seats through the s2s config' : 'No legacy server-side seat', active: legacy },
  {
    key: 'serverSide',
    iconName: 'server',
    tooltipText: serverSide ? 'Server-side and Spark for Pubs seats through the pubstack bidder' : 'No server-side or Spark for Pubs seat',
    active: serverSide,
  },
  { key: 'sparkDemand', iconName: 'pubstack', tooltipText: sparkDemand ? 'Spark Demand through the pubstack bidder' : 'No Spark Demand', active: sparkDemand },
]

export type AdmBidderGroups = {
  /** Bidders the page calls itself. */
  clientSide: string[]
  /** Server-side One (legacy) seats: on the page, and routed through Prebid Server by the `s2sConfig`. */
  legacyServerSide: string[]
  /** Node, Spark and Spark for Pubs seats: only in the server-side variant, reached through the pubstack bidder. */
  serverSide: string[]
}

/**
 * The bidders of a One stack, in the three sections of its bidder rule.
 *
 * The pubstack bidder is in none of them: it stands for the server-side seats, which are listed instead.
 */
export const admBidderGroups = (output: AdmStackOutput | undefined): AdmBidderGroups => {
  if (!output) return { clientSide: [], legacyServerSide: [], serverSide: [] }
  const routed = new Set(DEVICE_TYPES.flatMap((device) => output[device].prebidConfig.pbjsConfig.s2sConfig?.bidders ?? []))
  const adUnitBidders = uniq(DEVICE_TYPES.flatMap((device) => output[device].adUnits.flatMap((adUnit) => adUnit.bidders.map(({ bidder }) => bidder)))).filter((name) => name !== PUBSTACK_BIDDER)
  return {
    clientSide: adUnitBidders.filter((name) => !routed.has(name)),
    legacyServerSide: adUnitBidders.filter((name) => routed.has(name)),
    serverSide: uniqueBidders(output.serverside?.adUnits),
  }
}
